/* Handwritten-work progress timeline — AI Snapshots saved to a student's
 * profile during live classes. Used on the Student dashboard and the
 * Parent child view. */
import React, { useEffect, useState } from "react";
import { fmtDate } from "@/lib/dateUtils";
import api from "@/lib/api";
import {
  ScanLine, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, FileText,
} from "lucide-react";

function Verdict({ correct }) {
  if (correct === true)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 text-[10px] font-bold"><CheckCircle2 size={11} /> PASS</span>;
  if (correct === false)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-300 text-red-600 text-[10px] font-bold"><XCircle size={11} /> NEEDS WORK</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-600 text-[10px] font-bold"><AlertTriangle size={11} /> REVIEW</span>;
}

function Entry({ snap }) {
  const [open, setOpen] = useState(false);
  const r = snap.result || {};
  return (
    <div className="border border-neutral-200 bg-white hover:border-neutral-400 transition-colors" data-testid={`snapshot-entry-${snap.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full text-left flex items-stretch gap-0" data-testid={`snapshot-entry-toggle-${snap.id}`}>
        <img src={snap.image_url} alt="handwritten work" className="w-24 h-24 object-cover shrink-0 border-r border-neutral-200 bg-neutral-50" loading="lazy" />
        <div className="flex-1 p-3 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Verdict correct={r.correct} />
            {r.score != null && <span className="font-display font-bold text-lg leading-none">{r.score}<span className="text-neutral-400 text-xs">/100</span></span>}
            <span className="ml-auto text-[10px] font-mono text-neutral-400">{fmtDate(snap.created_at)}</span>
            {open ? <ChevronUp size={14} className="text-neutral-400" /> : <ChevronDown size={14} className="text-neutral-400" />}
          </div>
          <div className="text-xs text-neutral-600 mt-1.5 truncate">
            {snap.class_title || "Live class"}{snap.class_subject ? ` · ${snap.class_subject}` : ""}
          </div>
          {r.feedback && <div className="text-xs text-neutral-500 mt-1 line-clamp-2">{r.feedback}</div>}
        </div>
      </button>
      {open && (
        <div className="border-t border-neutral-200 p-4 space-y-3" data-testid={`snapshot-entry-detail-${snap.id}`}>
          <img src={snap.image_url} alt="handwritten work full" className="w-full max-h-72 object-contain border border-neutral-200 bg-neutral-50" />
          {snap.transcript && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-1 inline-flex items-center gap-1"><FileText size={11} /> Transcription</div>
              <div className="text-xs font-mono whitespace-pre-wrap bg-neutral-50 border border-neutral-200 p-3 text-neutral-700">{snap.transcript}</div>
            </div>
          )}
          {(r.mistakes || []).length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-red-500 mb-1">Mistakes spotted</div>
              <ul className="space-y-1">
                {r.mistakes.map((m, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-neutral-700">
                    <XCircle size={11} className="text-red-400 mt-0.5 shrink-0" /> {typeof m === "string" ? m : JSON.stringify(m)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {r.improvement_plan && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#002FA7] mb-1">Suggestions</div>
              <div className="text-xs text-neutral-700 leading-relaxed">{r.improvement_plan}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SnapshotTimeline({ studentId, title = "Handwritten work", compactLimit = 6 }) {
  const [snaps, setSnaps] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    (async () => {
      try {
        const { data } = await api.get(`/students/${studentId}/snapshots`);
        setSnaps(data.snapshots || []);
      } catch { setSnaps([]); }
    })();
  }, [studentId]);

  if (snaps === null) return null;

  const visible = showAll ? snaps : snaps.slice(0, compactLimit);
  return (
    <div className="bg-white border border-neutral-200 p-6" data-testid="snapshot-timeline">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#002FA7] font-bold inline-flex items-center gap-1.5">
            <ScanLine size={12} /> AI snapshots
          </div>
          <h2 className="font-display font-bold text-xl tracking-tighter mt-0.5">{title}</h2>
          <p className="text-xs text-neutral-500 mt-0.5">Paper work captured &amp; graded by AI during live classes.</p>
        </div>
        {snaps.length > 0 && (
          <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 border border-neutral-200 px-2.5 py-1">
            {snaps.length} snapshot{snaps.length === 1 ? "" : "s"}
          </div>
        )}
      </div>
      {snaps.length === 0 ? (
        <div className="text-sm text-neutral-500 py-8 text-center border border-dashed border-neutral-200" data-testid="snapshot-timeline-empty">
          No snapshots yet — captured paper work from live classes will appear here.
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-3">
            {visible.map((s) => <Entry key={s.id} snap={s} />)}
          </div>
          {snaps.length > compactLimit && (
            <button onClick={() => setShowAll((v) => !v)} data-testid="snapshot-timeline-toggle-all"
              className="mt-3 text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-4 py-2">
              {showAll ? "Show less" : `Show all ${snaps.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
